'use client';

import {
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import {
  type FieldErrors,
  useForm,
  type UseFormHandleSubmit,
  type UseFormRegister,
  zodResolver,
} from '@/libs';
import { CONTROL_KEYS, type ControlType } from '@/types/enums';

import { countSchema, type CountSchemaType } from '../utils';

type UseQuantitySelectorParams = {
  count: number;
  onSetCount: (value: number) => void;
  min?: number;
  max?: number;
  incrementStep?: number;
  decrementStep?: number;
};

type UseQuantitySelectorReturn = {
  increment: () => void;
  decrement: () => void;
  startIncrementHold: () => void;
  stopIncrementHold: () => void;
  startDecrementHold: () => void;
  stopDecrementHold: () => void;
  handleKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  handlePaste: (e: ClipboardEvent<HTMLInputElement>) => void;
  register: UseFormRegister<CountSchemaType>;
  handleSubmit: UseFormHandleSubmit<CountSchemaType, CountSchemaType>;
  errors: FieldErrors<CountSchemaType>;
  onValid: (data: CountSchemaType) => void;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
};

const DEFAULT_SPEED = 300;
const MIN_SPEED = 50;
const SPEED_STEP = 20;
const MIN = 1;
const MAX = 2000;
const STEP = 1;

export const useQuantitySelector = ({
  count,
  onSetCount,
  min = MIN,
  max = MAX,
  incrementStep = STEP,
  decrementStep = STEP,
}: UseQuantitySelectorParams): UseQuantitySelectorReturn => {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    clearErrors,
    setValue,
    getValues,
  } = useForm<CountSchemaType>({
    resolver: zodResolver(countSchema),
    defaultValues: { count },
  });

  const validateCount = useCallback(
    (value: number) => {
      if (value < min) {
        setError('count', {
          message: `A quantidade mínima é ${min}.`,
        });
        return;
      }

      if (value > max) {
        setError('count', {
          message: `A quantidade máxima é ${max}.`,
        });
        return;
      }

      clearErrors('count');
      return;
    },
    [min, max, setError, clearErrors],
  );

  useEffect(() => {
    setValue('count', count);
    validateCount(count);
  }, [count, setValue, validateCount, getValues]);

  const increment = () => {
    const nextCount = count + incrementStep;

    if (count >= max || nextCount > max) {
      setCount(max);
      setError('count', {
        message: `A quantidade máxima é ${max}.`,
      });
      return;
    }

    validateCount(nextCount);
    setCount(nextCount);
  };

  const decrement = () => {
    const nextCount = count - decrementStep;

    validateCount(nextCount);

    if (nextCount < min) return;
    setCount(nextCount);
  };

  const useHold = (action: () => void, isIncrement: boolean) => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const speedRef = useRef(DEFAULT_SPEED);

    const run = () => {
      if ((isIncrement && count >= max) || (!isIncrement && count <= min)) {
        clearTimeout(timeoutRef.current!);
        timeoutRef.current = null;
        speedRef.current = DEFAULT_SPEED;
        return;
      }
      action();
      speedRef.current = Math.max(MIN_SPEED, speedRef.current - SPEED_STEP);
      timeoutRef.current = setTimeout(run, speedRef.current);
    };

    const start = () => {
      if (timeoutRef.current) return;
      speedRef.current = DEFAULT_SPEED;
      timeoutRef.current = setTimeout(run, speedRef.current);
    };
    const stop = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      speedRef.current = DEFAULT_SPEED;
    };

    return { start, stop };
  };

  const { start: startIncrementHold, stop: stopIncrementHold } = useHold(increment, true);
  const { start: startDecrementHold, stop: stopDecrementHold } = useHold(
    decrement,
    false,
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const key = e.key as ControlType | string;
    const isDigit = /^[0-9]$/.test(key);
    if (!isDigit && !CONTROL_KEYS.includes(key as ControlType)) {
      e.preventDefault();
      return;
    }
    if (isDigit) {
      const current = e.currentTarget.value;
      const next = (current + key).replace(/^0+/, '') || '1';
      const nextLength = next.length;
      const maxLength = max.toString().length + 1;
      if (nextLength > maxLength) {
        e.preventDefault();
      }
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const paste = e.clipboardData.getData('text');
    if (!/^\d+$/.test(paste)) {
      e.preventDefault();
      return;
    }
    const current = (e.target as HTMLInputElement).value;
    const next = (current + paste).replace(/^0+/, '') || '1';
    if (parseInt(next, 10) > max) {
      e.preventDefault();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    const count = parseInt(value || '0', 10);

    onValid({ count });
  };

  const onValid = (data: CountSchemaType) => {
    validateCount(data.count);
    setCount(data.count);
  };

  const setCount = (value: number) => {
    setValue('count', value);
    onSetCount(value);
  };

  return {
    increment,
    decrement,
    startIncrementHold,
    stopIncrementHold,
    startDecrementHold,
    stopDecrementHold,
    handleKeyDown,
    handlePaste,
    register,
    handleSubmit,
    errors,
    onValid,
    handleChange,
  };
};
